/// <reference lib="dom" />
import type { CheckpointStore } from './multipart.js';
import type { UploadCheckpoint } from './resumable.js';

type Stored = UploadCheckpoint & { id: string };

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Checkpoints in an IndexedDB object store keyed by `id` (= checkpoint key).
 * `open` lets a caller that already owns a database (the capture extension
 * declares its schema in one place) pass its own opener.
 */
export function indexedDbCheckpointStore(
  storeName = 'uploads',
  open: () => Promise<IDBDatabase> = () => openUploadDb(),
): CheckpointStore {
  const withStore = async <T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await open();
    try {
      return await request(run(db.transaction(storeName, mode).objectStore(storeName)));
    } finally {
      db.close();
    }
  };
  return {
    async get(key) {
      const row = (await withStore('readonly', (s) => s.get(key))) as Stored | undefined;
      if (!row) return null;
      const { id: _id, ...checkpoint } = row;
      return checkpoint;
    },
    async put(checkpoint) {
      await withStore('readwrite', (s) => s.put({ ...checkpoint, id: checkpoint.key } satisfies Stored));
    },
    async delete(key) {
      await withStore('readwrite', (s) => s.delete(key));
    },
  };
}

export function openUploadDb(name = 'IvyUploads', storeName = 'uploads'): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(name, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(storeName)) req.result.createObjectStore(storeName, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
